import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { NotificationBell } from "@/components/NotificationBell";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LayoutDashboard, ClipboardList, Building2, MessageCircle, LogOut, Headphones, Users, UserCog,
  Layers, Tags, Radio, FileBarChart, Building, Shield, GitBranch, CircleDot, Menu, X, Palette, History, Bell,
} from "lucide-react";

export default function Layout() {
  const { user, logout, isSuper, isAdmin, hasPerm, tenantId, setTenantId, activeTenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const { branding } = useBranding();

  useEffect(() => {
    if (isSuper) api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
  }, [isSuper]);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isConsulta = user?.role === "CONSULTA";

  const nav = [
    isSuper && { to: "/tenants", label: "Empresas", icon: Building, testId: "nav-tenants", section: "Global" },
    isSuper && { to: "/branding", label: "Personalização", icon: Palette, testId: "nav-branding" },
    activeTenant && { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", section: "Operação" },
    activeTenant && hasPerm("view_os", "list_os") && { to: "/orders", label: "Ordens de Serviço", icon: ClipboardList, testId: "nav-orders" },
    activeTenant && hasPerm("view_os") && { to: "/customers", label: "Clientes", icon: Users, testId: "nav-customers" },
    activeTenant && hasPerm("reports") && { to: "/reports", label: "Relatórios", icon: FileBarChart, testId: "nav-reports" },
    activeTenant && hasPerm("manage_users") && { to: "/users", label: "Usuários", icon: UserCog, testId: "nav-users", section: "Administração" },
    activeTenant && hasPerm("manage_queues") && { to: "/queues", label: "Filas", icon: Layers, testId: "nav-queues" },
    activeTenant && hasPerm("manage_settings", "manage_queues") && { to: "/sectors", label: "Setores", icon: Building2, testId: "nav-sectors" },
    activeTenant && hasPerm("manage_settings", "manage_queues") && { to: "/os-types", label: "Tipos de OS", icon: Tags, testId: "nav-os-types" },
    activeTenant && hasPerm("manage_settings", "manage_queues") && { to: "/os-statuses", label: "Status da OS", icon: CircleDot, testId: "nav-os-statuses" },
    activeTenant && hasPerm("manage_channels") && { to: "/channels", label: "Canais", icon: Radio, testId: "nav-channels" },
    activeTenant && hasPerm("manage_queues") && { to: "/chat-flows", label: "Chat Flow", icon: GitBranch, testId: "nav-chat-flows" },
    activeTenant && isAdmin && { to: "/audit", label: "Histórico", icon: History, testId: "nav-audit" },
    !isConsulta && { to: "/notifications", label: "Notificações", icon: Bell, testId: "nav-notifications", section: "Ferramentas" },
    !isConsulta && { to: "/whatsapp", label: "WhatsApp Simulador", icon: MessageCircle, testId: "nav-whatsapp" },
  ].filter(Boolean);

  const tenantName = (isSuper ? tenants.find((t) => t.id === tenantId)?.nome_fantasia : user?.tenant?.nome_fantasia) || branding.company_name;
  const Logo = ({ cls, icon }) => (
    <div className={`${cls} rounded-xl flex items-center justify-center shrink-0 overflow-hidden ${branding.logo_url ? "bg-white" : "bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-lg shadow-emerald-500/20"}`}>
      {branding.logo_url ? <img src={branding.logo_url} alt="" className="w-full h-full object-contain" data-testid="brand-logo" /> : <Headphones className={icon} strokeWidth={2.5} />}
    </div>
  );

  return (
    <div className="h-dvh flex bg-slate-50 dark:bg-slate-950">
      {open && <div className="fixed inset-0 z-30 bg-slate-900/50 backdrop-blur-sm lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-overlay" />}

      <aside className={`fixed inset-y-0 left-0 z-40 w-72 max-w-[85vw] lg:static lg:w-64 lg:max-w-none lg:translate-x-0 transform transition-transform duration-200 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col min-h-0 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar">
        <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <Logo cls="w-10 h-10" icon="w-5 h-5 text-white" />
            <div className="min-w-0 flex-1">
              <h1 className="font-extrabold text-slate-900 dark:text-white tracking-tight truncate" data-testid="brand-system-name">{branding.system_name}</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate" data-testid="tenant-name">{tenantName || "Retaguarda"}</p>
            </div>
            {!isConsulta && <span className="hidden lg:block"><NotificationBell /></span>}
            <button className="lg:hidden text-slate-500 p-1" onClick={() => setOpen(false)} data-testid="sidebar-close-btn" aria-label="Fechar menu"><X className="w-5 h-5" /></button>
          </div>
          {isSuper && (
            <div className="mt-3">
              <Select value={tenantId || "none"} onValueChange={(v) => { setTenantId(v === "none" ? null : v); navigate(v === "none" ? "/tenants" : "/"); }}>
                <SelectTrigger className="h-9 text-xs" data-testid="tenant-switcher"><SelectValue placeholder="Selecionar empresa" /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-900">
                  <SelectItem value="none">— Visão global —</SelectItem>
                  {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome_fantasia}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <nav className="flex-1 min-h-0 px-3 py-3 space-y-0.5 overflow-y-auto">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.to}>
                {item.section && <p className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{item.section}</p>}
                <NavLink to={item.to} end={item.to === "/"} data-testid={item.testId}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 lg:py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"}`}>
                  <Icon className="w-4 h-4" strokeWidth={2.2} />{item.label}
                </NavLink>
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 mb-2">
            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm">
              {isSuper ? <Shield className="w-4 h-4" /> : user?.name?.[0]?.toUpperCase() ?? "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 dark:text-white truncate" data-testid="user-name">{user?.name}</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate" data-testid="user-role">{user?.role?.replace("_", " ")}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start text-slate-600 dark:text-slate-400 hover:text-rose-600"
            onClick={() => { if (window.confirm("Deseja sair do sistema?")) logout(); }} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-20 flex items-center gap-3 px-4 h-14 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-b border-slate-200 dark:border-slate-800" data-testid="mobile-header">
          <button onClick={() => setOpen(true)} className="p-2 -ml-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="sidebar-toggle-btn" aria-label="Abrir menu"><Menu className="w-6 h-6" /></button>
          <Logo cls="w-8 h-8" icon="w-4 h-4 text-white" />
          <div className="min-w-0 flex-1"><p className="font-extrabold text-sm leading-tight text-slate-900 dark:text-white truncate">{branding.system_name}</p><p className="text-[11px] text-slate-500 truncate">{tenantName || "Retaguarda"}</p></div>
          {!isConsulta && <NotificationBell compact />}
        </header>
        <main className="flex-1 overflow-auto"><Outlet /></main>
      </div>
    </div>
  );
}
